import { Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { openClose } from '@core/animations/animations';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select } from '@ngxs/store';
import {
	FileUpload,
	FileUploadState,
} from '@state/file-upload/fileUpload.state';
import { MatterDispatch } from '@state/matter';
import { UploadFileStatus } from '@state/utils/models';
import {
	createId,
	FILE_PACKAGE_TYPES,
	MatterFileTypeStrings,
	MortgageSupTypeStrings,
} from '@core/util/file';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ICON } from '@core/util/icon';
import {
	MatterDocumentType,
	MatterFile,
	MortgageDocumentSubType,
	MortgageMatterFile,
} from '@models/matter-file';
import { MortgageDocumentTypeTag } from '@core/util/tags';
import { SnackbarService } from '@services/ui/snackbar.service';
import { MatterUploadedFilesSelectors } from '@state/matter/uploaded-files/matter-uploaded-files.selectors';
import { TestHooks } from './modal.hooks';

const _openClose = openClose(350);

export interface UploadModalDocumentData {
	matterId: string;
	defaultFileType?: MatterDocumentType;
	replacingDocument?: MatterFile;
	subType?: MortgageDocumentSubType;
	lockFileType?: boolean;
	systemTags?: string[];
	contextId?: string;
	hideSubTypes?: boolean;
	runAiScraping?: boolean;
}

export interface UploadedFiles {
	fileName: string;
	fileId: string;
	file: File;
	fileType: MatterDocumentType;
	friendlyType: string;
	fileUploadStatus?: FileUpload;
	subType?: MortgageDocumentSubType;
	friendlySubType?: string;
	systemTags: string[];
}

interface FileCategoryType {
	type: MatterDocumentType;
	friendlyType: string;
}

@UntilDestroy()
@Component({
	selector: 'app-upload-document-modal',
	templateUrl: './modal.component.html',
	styleUrls: ['./modal.component.scss'],
	animations: [_openClose],
	standalone: false,
})
export class UploadDocumentModalComponent implements OnInit {
	testHooks = TestHooks;
	public ICON = ICON;
	public matterId;
	public failedUploading = false;
	public files: UploadedFiles[] = [];
	public fileType: MatterDocumentType = MatterDocumentType.MISC;
	public replacingDocument: MatterFile;
	public fileTypes: FileCategoryType[];
	public _fileTypes = MatterDocumentType;
	public mortgageSupTypes = MortgageDocumentSubType;
	public fileUploadStatuses = UploadFileStatus;
	public successfullyUploaded = false;

	public lockFileType = false;
	public systemTags: string[] = [];

	public showSubTypes = false;
	public subTypes = [];

	public disableUpload = false;
	public subType: MortgageDocumentSubType;
	public mortgageSupTypeStrings = MortgageSupTypeStrings;
	public readonly FILE_PACKAGE_TYPES = FILE_PACKAGE_TYPES;

	@Select(FileUploadState.getFileStatus) fileStatusFilter$: Observable<
		(filter: string) => FileUpload
	>;
	@Select(MatterUploadedFilesSelectors.getMortgageDocuments)
	mortgageDocFilterFn$: Observable<(id: string) => MortgageMatterFile[]>;

	constructor(
		public dialogRef: MatDialogRef<UploadDocumentModalComponent>,
		@Inject(MAT_DIALOG_DATA) public data: UploadModalDocumentData,
		private saleDispatch: MatterDispatch,
		private snackbarService: SnackbarService,
	) {}

	ngOnInit(): void {
		this.replacingDocument = this.data.replacingDocument;
		this.subType = this.data.subType;
		this.dialogRef.addPanelClass('upload-file-modal');
		this.matterId = this.data.matterId;
		this.fileType = this.data.defaultFileType ?? MatterDocumentType.MISC;
		this.lockFileType = this.data.lockFileType ?? false;
		this.systemTags = this.data.systemTags ?? [];

		this.fileTypes = Object.values(this._fileTypes)
			.map((type) => ({
				type,
				friendlyType: MatterFileTypeStrings[type] ?? type,
			}))
			.sort((a, b) => {
				const aType = a.friendlyType.toLowerCase();
				const bType = b.friendlyType.toLowerCase();

				if (aType > bType) return 1;
				if (aType < bType) return -1;
				return 0;
			});
	}

	fileSelected = ($event) => {
		const newFiles: UploadedFiles[] = Object.values($event).map((x: File) => {
			const baseTags = this.replacingDocument
				? this.replacingDocument.systemTags
				: this.systemTags;

			// Assign default subType for mortgage documents
			const defaultSubType =
				this.fileType === MatterDocumentType.MORTGAGE && !this.data.hideSubTypes
					? MortgageDocumentSubType.MORTGAGE_INSTRUCTIONS
					: undefined;

			return {
				fileName: x.name,
				fileId: createId(),
				file: x,
				fileType: this.fileType,
				friendlyType: MatterFileTypeStrings[this.fileType],
				systemTags: baseTags,
				subType: defaultSubType,
				friendlySubType: defaultSubType
					? MortgageSupTypeStrings[defaultSubType]
					: undefined,
			};
		});

		this.files.push(...newFiles);
		this.checkIfDisabled();
	};

	upload(): void {
		this.failedUploading = false;
		this.files.forEach((file) => {
			if (file.fileUploadStatus?.status !== UploadFileStatus.SUCCESS) {
				const newSystemTags = [...file.systemTags];

				if (file.fileType === MatterDocumentType.MORTGAGE && file.subType) {
					const newTag = MortgageDocumentTypeTag(file.subType);
					newSystemTags.push(newTag);
				}

				this.saleDispatch.UploadMatterFile(
					file.fileId,
					this.matterId,
					file.fileName,
					file.file,
					file.fileType,
					newSystemTags,
					this.data.runAiScraping,
				);
			}

			this.getFileStatus(file.fileId).subscribe((status) => {
				file.fileUploadStatus = status;

				if (file.fileUploadStatus.status === UploadFileStatus.FAILED) {
					this.failedUploading = true;
				}

				this.closeModal();
			});
		});
	}

	closeModal(): void {
		this.successfullyUploaded = true;
		this.files.forEach((file) => {
			if (!file.fileUploadStatus) {
				this.successfullyUploaded = false;
			}
			if (file.fileUploadStatus?.status !== UploadFileStatus.SUCCESS) {
				this.successfullyUploaded = false;
			}
		});

		if (this.successfullyUploaded) {
			const toastText =
				this.files.length > 1
					? `${this.files.length} documents`
					: `${this.files[0].fileName}`;
			this.snackbarService.openSnackBar({
				text: `<p><span class='font-bold'>${toastText}</span> uploaded</p>`,
				type: 'success',
			});
			this.dialogRef.close();
		}
	}

	updateFileName(fileId: string, $event) {
		this.files.forEach((file) => {
			if (file.fileId === fileId) {
				file.fileName = $event;
			}
		});
		this.checkIfDisabled();
	}

	updateFileType(fileId: string, $event) {
		this.files.forEach((file) => {
			if (file.fileId === fileId) {
				file.fileType = $event;
				file.friendlyType = MatterFileTypeStrings[file.fileType];
			}
		});
	}

	updateSubType(fileId: string, $event): void {
		this.files.forEach((file) => {
			if (file.fileId === fileId) {
				file.subType = $event;
				file.friendlySubType = MortgageSupTypeStrings[file.subType];
			}
		});

		this.checkIfDisabled();
	}

	remove(fileId: string) {
		this.files = this.files.filter((file) => file.fileId !== fileId);

		this.checkIfDisabled();
	}

	getFileStatus(id: string) {
		return this.fileStatusFilter$.pipe(
			untilDestroyed(this),
			map((func) => func(id)),
		);
	}

	close() {
		this.dialogRef.close();
	}

	checkIfDisabled(): void {
		const hasEmptyName = this.files.some((file) => file.fileName.trim() === '');
		const hasMissingSubType =
			this.showSubTypes &&
			!this.replacingDocument &&
			this.files.some((file) => !file.subType);

		this.disableUpload = hasEmptyName || hasMissingSubType;
	}

	getMortgageDoc$(id: string): Observable<MortgageMatterFile[]> {
		return this.mortgageDocFilterFn$.pipe(
			untilDestroyed(this),
			map((filterFn) => filterFn(id)),
		);
	}
}
